import React from "react";
import {
  ehDoadorFigado,
  rotuloModalidade,
} from "../../../shared/utils/modalidadeAtendimento";
import {
  LayoutDashboard,
  BarChart2,
  BarChart3,
  Contact2,
  ClipboardList,
  Hourglass,
  CircleCheck,
  Star,
  Mars,
  Venus,
  Clock,
} from "lucide-react";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useEvolucao } from "../../evolucao/context/EvolucaoContext";
import { theme } from "../../../shared/styles/theme";
import DashboardInsightsIA from "./DashboardInsightsIA";
import ListaPendencias from "../../pendencias/components/ListaPendencias";

// ═══════════════════════════════════════════════════════
// ACESSORES COM FALLBACK (inscricaoTx legado → campos planos)
// ═══════════════════════════════════════════════════════
function getNumeroRGCT(p: any): string | undefined {
  return p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct;
}
function getDataTransplante(p: any): string | undefined {
  return p.inscricaoTx?.dataTransplante || p.dataTransplante;
}
function getSituacaoEspecial(p: any): boolean {
  return (
    p.inscricaoTx?.situacaoEspecial === true || p.situacaoEspecial === true
  );
}

export default function DashboardStats() {
  const { pacientes } = usePacientes();
  const { evolucoes } = useEvolucao();

  // 📊 CALCULAR ESTATÍSTICAS DE CONSULTAS
  const hoje = new Date();
  const inicioSemana = new Date(hoje);
  inicioSemana.setDate(hoje.getDate() - hoje.getDay());
  inicioSemana.setHours(0, 0, 0, 0);

  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  // B323 Bloco 3 (AI_146): consulta cancelada não conta como consulta efetiva.
  const consultasSemana = evolucoes.filter((e) => {
    if (e.cancelado === true) return false;
    const dataConsulta = new Date(
      e.data?.includes("T") ? e.data : `${e.data}T12:00:00`
    );
    return dataConsulta >= inicioSemana && dataConsulta <= hoje;
  }).length;

  const consultasMes = evolucoes.filter((e) => {
    if (e.cancelado === true) return false;
    const dataConsulta = new Date(
      e.data?.includes("T") ? e.data : `${e.data}T12:00:00`
    );
    return dataConsulta >= inicioMes && dataConsulta <= hoje;
  }).length;

  const pacientesAtendidosMes = new Set(
    evolucoes
      .filter((e) => {
        if (e.cancelado === true) return false;
        const dataConsulta = new Date(
          e.data?.includes("T") ? e.data : `${e.data}T12:00:00`
        );
        return dataConsulta >= inicioMes && dataConsulta <= hoje;
      })
      .map((e) => e.pacienteId)
  ).size;

  // 📊 CALCULAR ESTATÍSTICAS

  const totalPacientes = pacientes.length;

  // 🏥 AMBULATORIAIS
  const ambulatoriais = pacientes.filter(
    (p) => p.tipoAtendimento?.toLowerCase() === "ambulatorial"
  ).length;

  // 🫀 FÍGADO - Detalhado
  const figadoTotal = pacientes.filter(
    (p) => p.tipoAtendimento?.toLowerCase() === "figado"
  );

  const figadoNaoInscritos = figadoTotal.filter(
    (p) => !getNumeroRGCT(p) && !getDataTransplante(p)
  ).length;

  const figadoInscritos = figadoTotal.filter(
    (p) => getNumeroRGCT(p) && !getDataTransplante(p)
  );

  const figadoTransplantados = figadoTotal.filter((p) =>
    getDataTransplante(p)
  ).length;

  // % com Situação Especial entre inscritos
  const figadoComSE = figadoInscritos.filter((p) =>
    getSituacaoEspecial(p)
  ).length;

  const percentualSE =
    figadoInscritos.length > 0
      ? Math.round((figadoComSE / figadoInscritos.length) * 100)
      : 0;

  // 🫘 RIM - Detalhado
  const rimTotal = pacientes.filter(
    (p) => p.tipoAtendimento?.toLowerCase() === "rim"
  );

  const rimNaoInscritos = rimTotal.filter(
    (p) => !getNumeroRGCT(p) && !getDataTransplante(p)
  ).length;

  const rimInscritos = rimTotal.filter(
    (p) => getNumeroRGCT(p) && !getDataTransplante(p)
  ).length;

  const rimTransplantados = rimTotal.filter((p) =>
    getDataTransplante(p)
  ).length;

  // 🥞 PÂNCREAS - Detalhado
  const pancreasTotal = pacientes.filter((p) => {
    const tipo = p.tipoAtendimento?.toLowerCase() || "";
    return (
      tipo === "pancreas" ||
      tipo === "pancreas_rim" ||
      tipo.includes("pancreas")
    );
  });

  const pancreasNaoInscritos = pancreasTotal.filter(
    (p) => !getNumeroRGCT(p) && !getDataTransplante(p)
  ).length;

  const pancreasInscritos = pancreasTotal.filter(
    (p) => getNumeroRGCT(p) && !getDataTransplante(p)
  ).length;

  const pancreasTransplantados = pancreasTotal.filter((p) =>
    getDataTransplante(p)
  ).length;

  // 🫱 DOADORES VIVOS DE FÍGADO — B351 (AI_152)
  // Antes desta contagem eles ficavam INVISÍVEIS no painel: as coortes filtram por
  // tipoAtendimento e nenhuma o alcançava. Não inflava nada (bom), mas também não
  // aparecia em lugar nenhum — e Leonardo já tem doadores cadastrados.
  const doadoresFigado = pacientes.filter((p) =>
    ehDoadorFigado(p.tipoAtendimento)
  );
  // Um doador "vinculado" é o que já tem receptor identificado na aba de avaliação
  // — é o que separa um cadastro em avaliação de um par doador-receptor definido.
  const doadoresVinculados = doadoresFigado.filter(
    (p: any) => p.avaliacaoDoador?.receptorId
  ).length;

  // 📊 TOTAIS GERAIS
  const totalTransplantes =
    figadoTotal.length + rimTotal.length + pancreasTotal.length;
  const totalInscritos =
    figadoInscritos.length + rimInscritos + pancreasInscritos;
  const totalTransplantados =
    figadoTransplantados + rimTransplantados + pancreasTransplantados;
  const totalNaoInscritos =
    figadoNaoInscritos + rimNaoInscritos + pancreasNaoInscritos;

  const porSexo = {
    masculino: pacientes.filter((p) => p.sexo === "M").length,
    feminino: pacientes.filter((p) => p.sexo === "F").length,
  };

  // 🎂 CALCULAR FAIXA ETÁRIA
  const calcularIdade = (dataNascimento: string) => {
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const m = hoje.getMonth() - nascimento.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  const porFaixaEtaria = {
    ate30: pacientes.filter((p) => calcularIdade(p.dataNascimento) <= 30)
      .length,
    de31a50: pacientes.filter((p) => {
      const idade = calcularIdade(p.dataNascimento);
      return idade >= 31 && idade <= 50;
    }).length,
    de51a70: pacientes.filter((p) => {
      const idade = calcularIdade(p.dataNascimento);
      return idade >= 51 && idade <= 70;
    }).length,
    acima70: pacientes.filter((p) => calcularIdade(p.dataNascimento) > 70)
      .length,
  };

  // 📈 CALCULAR IDADE MÉDIA
  const somaIdades = pacientes.reduce(
    (acc, p) => acc + calcularIdade(p.dataNascimento),
    0
  );
  const idadeMedia =
    totalPacientes > 0 ? Math.round(somaIdades / totalPacientes) : 0;

  // 📅 ÚLTIMOS CADASTROS
  const ultimosCadastros = [...pacientes]
    .sort(
      (a, b) =>
        new Date(b.dataCadastro).getTime() - new Date(a.dataCadastro).getTime()
    )
    .slice(0, 5);

  // 🎨 COMPONENTE DE CARD
  const StatCard = ({
    icon,
    titulo,
    valor,
    cor,
    subtitulo,
  }: {
    icon: string;
    titulo: string;
    valor: number | string;
    cor: string;
    subtitulo?: string;
  }) => (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.lg,
        padding: "24px",
        boxShadow: theme.shadows.md,
        border: `3px solid ${cor}`,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = theme.shadows.xl;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = theme.shadows.md;
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "-10px",
          right: "10px",
          fontSize: icon.length === 1 ? "90px" : "100px",
          opacity: icon.length === 1 ? 0.15 : 0.1,
          fontWeight: icon.length === 1 ? "900" : "normal",
          color: icon.length === 1 ? cor : undefined,
          fontFamily: theme.fonts.sans,
          lineHeight: 1,
        }}
      >
        {icon}
      </div>
      <div style={{ position: "relative", zIndex: 1 }}>
        <p
          style={{
            fontSize: "14px",
            color: theme.gray[600],
            fontWeight: "600",
            marginBottom: "8px",
            fontFamily: theme.fonts.sans,
          }}
        >
          {titulo}
        </p>
        <p
          style={{
            fontSize: "36px",
            fontWeight: "bold",
            color: cor,
            margin: 0,
            fontFamily: theme.fonts.sans,
          }}
        >
          {valor}
        </p>
        {subtitulo && (
          <p
            style={{
              fontSize: "12px",
              color: theme.gray[500],
              marginTop: "4px",
              fontFamily: theme.fonts.sans,
            }}
          >
            {subtitulo}
          </p>
        )}
      </div>
    </div>
  );

  // 📊 COMPONENTE DE BARRA DE PROGRESSO
  const ProgressBar = ({
    label,
    valor,
    total,
    cor,
    icon: Icon,
  }: {
    label: string;
    valor: number;
    total: number;
    cor: string;
    icon?: React.ElementType;
  }) => {
    const percentual = total > 0 ? Math.round((valor / total) * 100) : 0;

    return (
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: theme.gray[700],
              fontFamily: theme.fonts.sans,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {Icon && <Icon size={14} color={cor} />}
              {label}
            </span>
          </span>
          <span
            style={{
              fontSize: "14px",
              fontWeight: "bold",
              color: cor,
              fontFamily: theme.fonts.sans,
            }}
          >
            {valor} ({percentual}%)
          </span>
        </div>
        <div
          style={{
            width: "100%",
            height: "12px",
            background: theme.gray[200],
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${percentual}%`,
              height: "100%",
              background: cor,
              transition: "width 0.5s ease",
              borderRadius: "6px",
            }}
          />
        </div>
      </div>
    );
  };

  if (totalPacientes === 0) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          padding: "48px",
          textAlign: "center",
        }}
      >
        <BarChart2
          size={80}
          color={theme.gray[300]}
          style={{ marginBottom: "16px" }}
        />
        <h3
          style={{
            fontSize: "24px",
            color: theme.gray[700],
            marginBottom: "8px",
            fontFamily: theme.fonts.sans,
          }}
        >
          Nenhum dado para exibir
        </h3>
        <p style={{ color: theme.gray[500], fontFamily: theme.fonts.sans }}>
          Cadastre pacientes para ver as estatísticas
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* TÍTULO */}
      <div style={{ marginBottom: "24px" }}>
        <h2
          style={{
            fontSize: "28px",
            fontWeight: "bold",
            color: theme.gray[800],
            margin: 0,
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <LayoutDashboard size={22} style={{ marginRight: "8px" }} />
          Dashboard e Estatísticas
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: theme.gray[600],
            marginTop: "4px",
            fontFamily: theme.fonts.sans,
          }}
        >
          Visão geral dos pacientes cadastrados no sistema
        </p>
      </div>

      {/* CARDS PRINCIPAIS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        <StatCard
          icon="👥"
          titulo="Total de Pacientes"
          valor={totalPacientes}
          cor={theme.primary[600]}
          subtitulo={`${totalTransplantados} transplantados`}
        />
        <StatCard
          icon="F"
          titulo="TX Fígado"
          valor={figadoTotal.length}
          cor="#10b981"
          subtitulo={`${figadoTransplantados} transplantados`}
        />
        <StatCard
          icon="R"
          titulo="TX Rim"
          valor={rimTotal.length}
          cor="#be185d"
          subtitulo={`${rimTransplantados} transplantados`}
        />
        <StatCard
          icon="P"
          titulo="TX Pâncreas"
          valor={pancreasTotal.length}
          cor="#f59e0b"
          subtitulo={`${pancreasTransplantados} transplantados`}
        />
        {/* B351 (AI_152) — só aparece quando existe doador cadastrado, para não
            somar um card vazio ao painel de quem não faz intervivos. */}
        {doadoresFigado.length > 0 && (
          <StatCard
            icon="D"
            titulo="Doadores (intervivos)"
            valor={doadoresFigado.length}
            cor="#14b8a6"
            subtitulo={`${doadoresVinculados} com receptor vinculado`}
          />
        )}
      </div>

      {/* SEGUNDA LINHA DE CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        <StatCard
          icon="A"
          titulo="Ambulatorial"
          valor={ambulatoriais}
          cor={theme.primary[500]}
        />
        <StatCard
          icon="I"
          titulo="Idade Média"
          valor={`${idadeMedia} anos`}
          cor={theme.primary[600]}
        />
        <StatCard
          icon="📋"
          titulo="Consultas na Semana"
          valor={consultasSemana}
          cor="#8b5cf6"
          subtitulo={`${consultasMes} no mês`}
        />
        <StatCard
          icon="👤"
          titulo="Pacientes Atendidos (Mês)"
          valor={pacientesAtendidosMes}
          cor="#059669"
          subtitulo={`de ${totalPacientes} cadastrados`}
        />
      </div>

      {/* DETALHAMENTO POR ÓRGÃO */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "24px",
          marginBottom: "32px",
        }}
      >
        {/* FÍGADO DETALHADO */}
        {figadoTotal.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              boxShadow: theme.shadows.md,
              padding: "24px",
              border: "3px solid #10b981",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                color: "#10b981",
                marginBottom: "20px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Fígado - Detalhamento
            </h3>
            <ProgressBar
              label="Não Inscritos"
              icon={ClipboardList}
              valor={figadoNaoInscritos}
              total={figadoTotal.length}
              cor="#6b7280"
            />
            <ProgressBar
              label="Na Lista de Espera"
              icon={Hourglass}
              valor={figadoInscritos.length}
              total={figadoTotal.length}
              cor="#f59e0b"
            />
            <ProgressBar
              label="Transplantados"
              icon={CircleCheck}
              valor={figadoTransplantados}
              total={figadoTotal.length}
              cor="#10b981"
            />

            {figadoInscritos.length > 0 && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  background: "#fef3c7",
                  borderRadius: theme.borderRadius.md,
                  border: "2px solid #f59e0b",
                }}
              >
                <p
                  style={{
                    fontSize: "13px",
                    color: "#92400e",
                    margin: 0,
                    fontFamily: theme.fonts.sans,
                    fontWeight: "600",
                  }}
                >
                  ⭐ Situação Especial: {figadoComSE} de{" "}
                  {figadoInscritos.length} ({percentualSE}%)
                </p>
              </div>
            )}
          </div>
        )}

        {/* RIM DETALHADO */}
        {rimTotal.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              boxShadow: theme.shadows.md,
              padding: "24px",
              border: "3px solid #be185d",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                color: "#be185d",
                marginBottom: "20px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Rim - Detalhamento
            </h3>
            <ProgressBar
              label="Não Inscritos"
              icon={ClipboardList}
              valor={rimNaoInscritos}
              total={rimTotal.length}
              cor="#6b7280"
            />
            <ProgressBar
              label="Na Lista de Espera"
              icon={Hourglass}
              valor={rimInscritos}
              total={rimTotal.length}
              cor="#f59e0b"
            />
            <ProgressBar
              label="Transplantados"
              icon={CircleCheck}
              valor={rimTransplantados}
              total={rimTotal.length}
              cor="#be185d"
            />
          </div>
        )}

        {/* PÂNCREAS DETALHADO */}
        {pancreasTotal.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.xl,
              boxShadow: theme.shadows.md,
              padding: "24px",
              border: "3px solid #f59e0b",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                color: "#f59e0b",
                marginBottom: "20px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Pâncreas - Detalhamento
            </h3>
            <ProgressBar
              label="Não Inscritos"
              icon={ClipboardList}
              valor={pancreasNaoInscritos}
              total={pancreasTotal.length}
              cor="#6b7280"
            />
            <ProgressBar
              label="Na Lista de Espera"
              icon={Hourglass}
              valor={pancreasInscritos}
              total={pancreasTotal.length}
              cor="#f59e0b"
            />
            <ProgressBar
              label="Transplantados"
              icon={CircleCheck}
              valor={pancreasTransplantados}
              total={pancreasTotal.length}
              cor="#f59e0b"
            />
          </div>
        )}
      </div>

      {/* GRÁFICOS COMPLEMENTARES */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "24px",
        }}
      >
        {/* DISTRIBUIÇÃO POR SEXO */}
        <div
          style={{
            background: "white",
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: theme.gray[800],
              marginBottom: "20px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Contact2 size={18} style={{ marginRight: "8px" }} /> Distribuição
            por Sexo
          </h3>
          <ProgressBar
            label="Masculino"
            icon={Mars}
            valor={porSexo.masculino}
            total={totalPacientes}
            cor={theme.primary[600]}
          />
          <ProgressBar
            label="Feminino"
            icon={Venus}
            valor={porSexo.feminino}
            total={totalPacientes}
            cor="#ec4899"
          />

          <div
            style={{
              marginTop: "20px",
              padding: "12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
            }}
          >
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[600],
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              <strong>Proporção M/F:</strong>{" "}
              {porSexo.feminino > 0
                ? (porSexo.masculino / porSexo.feminino).toFixed(2)
                : "N/A"}
            </p>
          </div>
        </div>

        {/* DISTRIBUIÇÃO POR FAIXA ETÁRIA */}
        <div
          style={{
            background: "white",
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: theme.gray[800],
              marginBottom: "20px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <BarChart3 size={18} style={{ marginRight: "8px" }} /> Distribuição
            por Faixa Etária
          </h3>
          <ProgressBar
            label="Até 30 anos"
            valor={porFaixaEtaria.ate30}
            total={totalPacientes}
            cor="#10b981"
          />
          <ProgressBar
            label="31-50 anos"
            valor={porFaixaEtaria.de31a50}
            total={totalPacientes}
            cor={theme.primary[500]}
          />
          <ProgressBar
            label="51-70 anos"
            valor={porFaixaEtaria.de51a70}
            total={totalPacientes}
            cor="#f59e0b"
          />
          <ProgressBar
            label="Acima de 70"
            valor={porFaixaEtaria.acima70}
            total={totalPacientes}
            cor="#ef4444"
          />

          <div
            style={{
              marginTop: "20px",
              padding: "12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
            }}
          >
            <p
              style={{
                fontSize: "12px",
                color: theme.gray[600],
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              <strong>Idade Média:</strong> {idadeMedia} anos
            </p>
          </div>
        </div>
      </div>

      {/* ÚLTIMOS CADASTROS */}
      {ultimosCadastros.length > 0 && (
        <div
          style={{
            background: "white",
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: "24px",
            marginTop: "24px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: theme.gray[800],
              marginBottom: "16px",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Clock size={16} style={{ marginRight: "8px" }} /> Últimos Cadastros
            <span
              style={{
                fontSize: "12px",
                fontWeight: "normal",
                color: theme.gray[500],
              }}
            >
              (5 mais recentes)
            </span>
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {ultimosCadastros.map((paciente, index) => {
              let tipoCor = theme.gray[400];
              let tipoNome = paciente.tipoAtendimento;
              const tipoLower = paciente.tipoAtendimento?.toLowerCase() || "";
              if (tipoLower === "ambulatorial") {
                tipoCor = theme.primary[500];
                tipoNome = "Ambulatorial";
              } else if (tipoLower === "figado") {
                tipoCor = "#16a34a";
                tipoNome = "Fígado";
              } else if (tipoLower === "rim") {
                tipoCor = "#dc2626";
                tipoNome = "Rim";
              } else if (tipoLower.includes("pancrea")) {
                tipoCor = "#ca8a04";
                tipoNome = "Pâncreas";
              } else if (ehDoadorFigado(tipoLower)) {
                // B351 (AI_152): sem este ramo o doador aparecia com o valor CRU
                // ("doador_figado") na lista de últimos cadastros.
                tipoCor = "#14b8a6";
                tipoNome = rotuloModalidade(tipoLower);
              }

              return (
                <div
                  key={paciente.id}
                  style={{
                    padding: "16px",
                    background: index === 0 ? theme.primary[50] : "#f9fafb",
                    borderRadius: theme.borderRadius.md,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border:
                      index === 0 ? `2px solid ${theme.primary[200]}` : "none",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.primary[100];
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      index === 0 ? theme.primary[50] : "#f9fafb";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: tipoCor,
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <p
                        style={{
                          fontWeight: "600",
                          color: theme.gray[800],
                          margin: 0,
                          fontFamily: theme.fonts.sans,
                          fontSize: "14px",
                        }}
                      >
                        {paciente.nome}
                      </p>
                      <p
                        style={{
                          fontSize: "12px",
                          color: theme.gray[500],
                          margin: 0,
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Cadastro:{" "}
                        {new Date(paciente.dataCadastro).toLocaleDateString(
                          "pt-BR"
                        )}
                      </p>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      padding: "6px 12px",
                      borderRadius: theme.borderRadius.full,
                      background:
                        tipoLower === "figado"
                          ? theme.tipoAtendimento.figado.bg
                          : tipoLower === "rim"
                          ? theme.tipoAtendimento.rim.bg
                          : tipoLower.includes("pancrea")
                          ? theme.tipoAtendimento.pancreas.bg
                          : theme.tipoAtendimento.ambulatorial.bg,
                      color:
                        tipoLower === "figado"
                          ? theme.tipoAtendimento.figado.text
                          : tipoLower === "rim"
                          ? theme.tipoAtendimento.rim.text
                          : tipoLower.includes("pancrea")
                          ? theme.tipoAtendimento.pancreas.text
                          : theme.tipoAtendimento.ambulatorial.text,
                      border: `1px solid ${
                        tipoLower === "figado"
                          ? theme.tipoAtendimento.figado.border
                          : tipoLower === "rim"
                          ? theme.tipoAtendimento.rim.border
                          : tipoLower.includes("pancrea")
                          ? theme.tipoAtendimento.pancreas.border
                          : theme.tipoAtendimento.ambulatorial.border
                      }`,
                      whiteSpace: "nowrap",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {tipoNome}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SEÇÃO DE IA - INSIGHTS */}
      <DashboardInsightsIA />
      <ListaPendencias />
    </div>
  );
}
